import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigationStack } from '../context/NavigationStackContext';
import { useManageMatterRequestContext } from '../context/ManageMatterRequest';
import { useFetchUsers } from './useFetchUsers';
import { IUser } from '../interface/user';
import { IMatterRequest } from '../interface/matterRequest';
import { EntityStatus } from '../interface/common';

export type MatterRequestUserField = 'owner' | 'responsible';

export const useSelectMatterRequestUser = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const field = (location.state?.field as MatterRequestUserField | undefined) ?? 'owner';
  const { goBack } = useNavigationStack();
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');

  const {
    data: userList,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchUsers({
    pageSize: 10,
    sortBy: ['firstNames:ASC'],
    search: searchValue,
    extraFilters: { status: EntityStatus.ACTIVE },
  });

  const refreshUserList = () => {
    queryClient.removeQueries({ queryKey: ['users-list'] });
    return refetch();
  };

  const { ref: paginationRef, inView } = useInView({ threshold: 0.5 });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && userList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, userList]);

  const title = field === 'owner' ? t('matterRequest:owner') : t('matterRequest:responsiblePerson');
  const currentValue =
    field === 'owner' ? matterRequest?.ownerUserId : matterRequest?.responsibleUserId;

  const handleSelectUser = (user: IUser) => {
    updateManageMatterRequest({
      ...matterRequest,
      ...(field === 'owner'
        ? { ownerUserId: user.id, ownerUser: user, isOwnerSetByUser: true }
        : { responsibleUserId: user.id, responsibleUser: user, isResponsibleSetByUser: true }),
    } as unknown as IMatterRequest);
    goBack();
  };

  return {
    t,
    title,
    userList,
    isLoading,
    isFetching,
    searchValue,
    setSearchValue,
    paginationRef,
    hasNextPage,
    refreshUserList,
    handleSelectUser,
    currentValue,
  };
};
